/*
    Controller to get the deployments (API terminology) / code history (UI terminology) for
        for a given  devicegroup (API terminology) / devicegroup (UI terminology)
    Invoking:
        Radio.request(
            'global',
            'show_devicegroup_code_history',
            {{devicegroup}}
        );
    Arguments:
        devicegroup: v5 model "devicegroup"
            models/v5/devicegroup.js
    Result:
        views/devicegroup_code_history.js will be instantiated and its view rendered in app_layout's
        "container" region.
        If devicegroup.type is "development_devicegroup", the Development Device Group Deployment History view will show
        If devicegrouptype is "pre_factoryfixture_devicegroup", the Pre-Factory Fixture Devicegroup Deployment History view will show
            The pre_factoryfixure_devicegroup view has tabs.
*/

import _ from 'underscore';
import Radio from 'backbone.radio';
import Handlebars from 'handlebars';
import Marionette from 'backbone.marionette';
import moment from 'moment';
import StandardListLayout from '@/views/standard_list_layout';
import CollectionTableView from '@/views/collection_table';
import DeploymentItemView from '@/views/product_deployment_item';
import PaginationControls from '@/views/pagination_controls';
import EmptyView from '@/views/code_history_empty_view';
import Button from '@/views/button';
import bulk_controls_template from '@/../templates/devicegroup-deployments-controls.hbs';
import collection_template from '@/../templates/collection-table.hbs';


var apiCh = Radio.channel('api');
var globalCh = Radio.channel('global');
var routerCh = Radio.channel('router');



// Basic template for deployment columns
// Some devicegroup types will add a manage column
const col = Object.freeze({
    type: {
        name: 'Deployment Type',
        class_name: 'status'
    },
    date: {
        name: 'Deployment Date',
        class_name: 'deployment-date capitalize',
        sortable: false,
        sort_by: 'created_at',
    },
    sha: {
        name: 'sha',
        class_name: 'deployment-sha capitalize',
    },
    deployed_by: {
        name: 'Deployed By',
        class_name: 'deployed-by capitalize'
    },
    manage: {
        name: 'Manage',
        class_name: 'actions capitalize'
    }
});
// Collections can have whatever in them, so must filter at a view level
function deployment_matches_product_and_devicegroup(product, devicegroup, deployment) {
    return (deployment.rel.get('product') === product
        && deployment.rel.get('devicegroup') == devicegroup);
}
// View comparator
function by_created_at(deployment) {
    return -moment(deployment.get('created_at'));
}
function load_data(devicegroup, product) {
    devicegroup.load()
        // .done(product.load)
        .done(show_devicegroup_code_history.bind(this, devicegroup, product));
}
    // The controller
function show_devicegroup_code_history(devicegroup, product) {
    var deployments = apiCh.request('deployment');
    var deployments_filter = _.partial(
        deployment_matches_product_and_devicegroup,
        product,
        devicegroup
    );
    function open_create_deployment_dialog() {
        globalCh.request('show_create_deployment_dialog', devicegroup);
    }
    function open_update_devices_dialog() {
        globalCh.request('show_upgrade_devices_dialog', devicegroup);
    }
    var CodeHistoryLayoutView = StandardListLayout.extend({
        templateContext: function(){
            // Hide these for now, but product might ask for them in the future, so leaving all other work in for tabs.
            var show_factory_tabs = false;
            var title = devicegroup.get('name') + ' Deployments';
            return {
                show_factory_tabs: show_factory_tabs,
                title: title,
            };
        },
        childViewEvents: {
            'page': function (filter) {
                var list_view = this.getRegion('content').currentView;
                list_view.setFilter(filter);
            }
        },
        show_upgrade_devices_button: function(devicegroup){
            var upgrade_devices_button = new Button({
                text: 'Upgrade Devices to Current Deployment',
                icon: '',
                action: function () {
                    globalCh.request(
                        'show_upgrade_devices_dialog',
                        devicegroup);
                }
            });
            var self = this;
            devicegroup.fetch().done(function(){
                if (devicegroup.rel.get('min_supported_deployment')) {
                    if (self.isDestroyed()) return;
                    self.showChildView('bulk_controls', upgrade_devices_button);
                }
            });
        },
        show_test_factory_deployments: function (){
            var columns = [
                col.type,
                col.date,
                col.sha,
                col.deployed_by
            ];
            if ( devicegroup.type === 'pre_factoryfixture_devicegroup' ) {
                columns.push(col.manage);
            }
            var pagination_controls = this.getRegion('pagination_controls').currentView;
            var code_history_table_view = new CollectionTableView({
                template: Handlebars.compile(collection_template),
                collection: deployments,
                childView: DeploymentItemView,
                class_name: 'code-history-list',
                default_sort_column: 1,
                default_sort_desc: true,
                columns: columns,
                emptyView: EmptyView,
                emptyViewOptions: {
                    devicegroup: devicegroup,
                },
                viewComparator: by_created_at,
                childViewOptions: {
                    can_view_code: true,
                    show_polite_status: true
                }
            });

            // Using _.partial allows the filter to simply take in a deployment later as the first argument and filter off that
            code_history_table_view.filter = _.partial(
                deployment_matches_product_and_devicegroup,
                product,
                devicegroup
            );

            return deployments.fetch({
                data: {
                    'filter[devicegroup.id]': devicegroup.id,
                }
            })
            .done(pagination_controls.update_prev_next_from_response)
            .then(function () {
                if (this.isDestroyed()) return;
                this.showChildView('content', code_history_table_view);
            }.bind(this));
        },
        show_factory_deployments: function () {
            var columns = [
                col.type,
                col.date,
                col.sha,
                col.deployed_by
            ];
            var pagination_controls = this.getRegion('pagination_controls').currentView;
            var code_history_table_view = new CollectionTableView({
                template: Handlebars.compile(collection_template),
                collection: deployments,
                childView: DeploymentItemView,
                class_name: 'code-history-list',
                default_sort_column: 1,
                default_sort_desc: true,
                columns: columns,
                emptyView: Marionette.View.extend({
                    template: Handlebars.compile('No deployment history for this Device Group.'),
                }),
                viewComparator: by_created_at,
                childViewOptions: {
                    can_view_code: true,
                    show_polite_status: true
                }
            });

            // Using _.partial allows the filter to simply take in a deployment later as the first argument and filter off that
            code_history_table_view.filter = _.partial(
                deployment_matches_product_and_devicegroup,
                product,
                devicegroup
            );

            return deployments.fetch({
                data: {
                    'filter[devicegroup.id]': devicegroup.id,
                }
            })
                .done(pagination_controls.update_prev_next_from_response)
                .then(function () {
                    if (this.isDestroyed()) return;
                    this.showChildView('content', code_history_table_view);
                }.bind(this));
        },
        show_devicegroup_deployments: function (){
            var columns = [
                col.type,
                col.date,
                col.sha,
                col.deployed_by
            ];
            if ( devicegroup.type === 'development_devicegroup' ) {
                columns.push(col.manage);
            }
            // Why is production devicegroup history using this??
            var can_view_code = !(devicegroup.type === 'production_devicegroup');
            var pagination_controls = this.getRegion('pagination_controls').currentView;
            var code_history_table_view = new CollectionTableView({
                template: Handlebars.compile(collection_template),
                collection: deployments,
                childView: DeploymentItemView,
                class_name: 'code-history-list',
                default_sort_column: 1,
                default_sort_desc: true,
                columns: columns,
                emptyView: EmptyView,
                emptyViewOptions: {
                    devicegroup: devicegroup,
                },
                viewComparator: by_created_at,
                childViewOptions: {
                    can_view_code: can_view_code,
                    show_polite_status: true
                }
            });
            // Using _.partial allows the filter to simply take in a deployment later as the first argument and filter off that
            code_history_table_view.filter = _.partial(deployment_matches_product_and_devicegroup, product, devicegroup);

            // Fetch the deployments for the given devicegroup, populate the pagination buttons if need be,
            // and render the tableview.
            return deployments.fetch({
                data: {
                    'filter[devicegroup.id]': devicegroup.id
                }
            })
            .done(pagination_controls.update_prev_next_from_response)
            .then(function () {
                if (this.isDestroyed()) return;
                this.showChildView('content', code_history_table_view);
            }.bind(this));
        },
        show_production_deployments: function (options){
            options = options || {};

            var columns = [
                col.type,
                col.date,
                col.sha,
                col.deployed_by,
                col.manage
            ];

            var pagination_controls = this.getRegion('pagination_controls').currentView;
            var code_history_table_view = new CollectionTableView({
                template: Handlebars.compile(collection_template),
                collection: deployments,
                childView: DeploymentItemView,
                class_name: 'code-history-list',
                default_sort_column: 1,
                default_sort_desc: true,
                columns: columns,
                emptyView: EmptyView,
                emptyViewOptions: {
                    devicegroup: devicegroup
                },
                viewComparator: by_created_at,
                childViewOptions: {
                    can_view_code: true,
                    show_polite_status: true
                }
            });

            // Using _.partial allows the filter to simply take in a deployment later as the first argument and filter off that
            code_history_table_view.filter = _.partial(deployment_matches_product_and_devicegroup, product, devicegroup);
            return deployments.fetch({
                data: {
                    'filter[devicegroup.id]': devicegroup.id,
                }
            })
            .done(pagination_controls.update_prev_next_from_response)
            .then(function () {
                if (this.isDestroyed()) return;
                this.showChildView('content', code_history_table_view);
            }.bind(this));
        },
        show_dut_deployments: function (options){
            options = options || {};

            var columns = [
              col.type,
              col.date,
              col.sha,
              col.deployed_by,
              col.manage
            ];

            let active = globalCh.request('active_account');
            let primary = globalCh.request('primary_account');
            let can_view_code =  active.permits(primary, 'view_factory_code') && devicegroup.type.indexOf('pre_') === 0;
            var pagination_controls = this.getRegion('pagination_controls').currentView;
            var code_history_table_view = new CollectionTableView({
                template: Handlebars.compile(collection_template),
                collection: deployments,
                childView: DeploymentItemView,
                class_name: 'code-history-list',
                default_sort_column: 1,
                default_sort_desc: true,
                columns: columns,
                emptyView: EmptyView,
                emptyViewOptions: {
                    devicegroup: devicegroup
                },
                viewComparator: by_created_at,
                childViewOptions: {
                    can_view_code: can_view_code,
                    show_polite_status: true
                }
            });

            // Using _.partial allows the filter to simply take in a deployment later as the first argument and filter off that
            code_history_table_view.filter = _.partial(deployment_matches_product_and_devicegroup, product, devicegroup);
            return deployments.fetch({
                data: {
                    'filter[devicegroup.id]': devicegroup.id,
                }
            })
            .done(pagination_controls.update_prev_next_from_response)
            .then(function () {
                if (this.isDestroyed()) return;
                this.showChildView('content', code_history_table_view);
            }.bind(this));
        },
    });
    var create_new_deployment_button = new Button({
        text: 'New Deployment',
        icon: 'fa fa-plus-circle',
        primary: true,
        action: open_create_deployment_dialog
    });
    var bulk_controls = new (Marionette.View.extend({
        template: Handlebars.compile(bulk_controls_template),
        ui: {
            update_devices: 'button.update-devices',
        },
        events: {
            'click @ui.update_devices': open_update_devices_dialog
        },
        onRender: function() {
            this.listenTo(
                deployments,
                'add remove sync',
                this.set_update_devices_button_state);
            this.set_update_devices_button_state();
        },
        set_update_devices_button_state: function() {
            if (deployments.filter(deployments_filter).length) {
                this.ui.update_devices.removeClass('disabled');
                this.ui.update_devices.attr('disabled', false);
            } else {
                this.ui.update_devices.addClass('disabled');
                this.ui.update_devices.attr('disabled', true);
            }
        }


    }));

    var code_history_layout = new CodeHistoryLayoutView();
    var pagination_controls = new PaginationControls({
        collection: deployments
    });
    var app_container = globalCh.request('app_layout');


    // Render the code history layout in the app container, and the pagination controls in the code history layout
    // This must be done before fetching so the table view has existing DOM elements to render into
    app_container.showChildView('container', code_history_layout);
    code_history_layout.showChildView('pagination_controls', pagination_controls);

    // Show the bulk controls including add deployment and update devices
    var active = globalCh.request('active_account');
    var primary = globalCh.request('primary_account');
    var required_permission = 'create_' + devicegroup.type;
    if (devicegroup.type === 'factoryfixture_devicegroup' || devicegroup.type === 'dut_devicegroup') {
        required_permission = 'create_factory_devicegroup';
    }
    if (devicegroup.type === 'pre_factoryfixture_devicegroup' || devicegroup.type === 'pre_dut_devicegroup') {
        required_permission = 'create_pre_factory_devicegroup';
    }
    var can_create_new_deployment = 
        // Right devicegroup type
        [   
            'pre_factoryfixture_devicegroup',
            'pre_production_devicegroup',
            'pre_dut_devicegroup',
            'development_devicegroup',
            'production_devicegroup',
            'dut_devicegroup',
            'factoryfixture_devicegroup'
        ].includes(devicegroup.type)
        && active.permits(primary, required_permission);
    if (can_create_new_deployment) {
        code_history_layout.showChildView('create_item', create_new_deployment_button);
    }

    // Each function creates a new table view, updates deployments collection, and then populates the code_history_layout
    // with the new table view
    if (devicegroup.type === 'pre_factoryfixture_devicegroup') {
        code_history_layout.show_test_factory_deployments().then(function(){
            if (code_history_layout.isDestroyed()) return;
            code_history_layout.showChildView('bulk_controls', bulk_controls);
        });
    } else if (devicegroup.type === 'pre_production_devicegroup') {
        code_history_layout.show_production_deployments({ type: 'pre_production_devicegroup'}).then(function(){
            if (code_history_layout.isDestroyed()) return;
            code_history_layout.showChildView('bulk_controls', bulk_controls);
        });
    } else if (devicegroup.type === 'production_devicegroup') {
        code_history_layout.show_production_deployments().then(function () {
            if (code_history_layout.isDestroyed()) return;
            code_history_layout.showChildView('bulk_controls', bulk_controls);
        });
    } else if (devicegroup.type === 'factoryfixture_devicegroup') {
        code_history_layout.show_production_deployments().then(function () {
            if (code_history_layout.isDestroyed()) return;
            code_history_layout.showChildView('bulk_controls', bulk_controls);
        });
    } else if (devicegroup.type === 'dut_devicegroup') {
        code_history_layout.show_dut_deployments().then(function () {
            code_history_layout.showChildView('bulk_controls', bulk_controls);
            if (code_history_layout.isDestroyed()) return;
        });
    } else if (devicegroup.type === 'pre_dut_devicegroup') {
        code_history_layout.showChildView('bulk_controls', bulk_controls);
        code_history_layout.show_dut_deployments().then(function () {
            if (code_history_layout.isDestroyed()) return;
        });
    }  else {
        code_history_layout.show_devicegroup_deployments().then(function(){
            if (code_history_layout.isDestroyed()) return;
            code_history_layout.showChildView('bulk_controls', bulk_controls);
        });
    }

    switch ( devicegroup.type ) {
        case devicegroup.TYPES_MAP.production:
            globalCh.request('show_devicegroup_secondary_nav',
                devicegroup, {
                selected: 'deployment-history'
            });
            globalCh.request('set_selection', {
                type: devicegroup.type,
                id: devicegroup.id,
                v5_object: devicegroup,
                area: 'production'
            });
            routerCh.request('set_location', 'devicegroup_code_history', product,  devicegroup);
            break;
        case devicegroup.TYPES_MAP.factoryfixture:
            globalCh.request('show_devicegroup_secondary_nav',
                devicegroup, {
                selected: 'deployment-history'
            });
            globalCh.request('set_selection', {
                type: devicegroup.type,
                id: devicegroup.id,
                v5_object: devicegroup,
                area: 'production'
            });
            routerCh.request('set_location', 'factory_code_history', product,  devicegroup);
            break;
        case devicegroup.TYPES_MAP.pre_production:
            globalCh.request('show_devicegroup_secondary_nav', devicegroup, { selected: 'deployment-history' });
            globalCh.request('set_selection', { type: devicegroup.type, id: devicegroup.id, v5_object: devicegroup, area: 'test' });
            routerCh.request('set_location', 'devicegroup_code_history', product, devicegroup);
            break;
        case devicegroup.TYPES_MAP.pre_dut:
            globalCh.request('show_devicegroup_secondary_nav', devicegroup, { selected: 'deployment-history' });
            globalCh.request('set_selection', { type: devicegroup.type, id: devicegroup.id, v5_object: devicegroup, area: 'test' });
            routerCh.request('set_location', 'devicegroup_code_history', product, devicegroup);
            break;
        case devicegroup.TYPES_MAP.dut:
            globalCh.request('show_devicegroup_secondary_nav',
                devicegroup, {
                selected: 'deployment-history'
            });
            globalCh.request('set_selection', {
                type: devicegroup.type,
                id: devicegroup.id,
                v5_object: devicegroup,
                area: 'production'
            });
            routerCh.request('set_location', 'devicegroup_code_history', product,  devicegroup);
            break;
        default:
            globalCh.request('show_dev_devicegroup_secondary_nav', devicegroup, { selected: 'deployment-history' });
            globalCh.request('set_selection', {
                type: devicegroup.type,
                id: devicegroup.id,
                v5_object: devicegroup
            });
            routerCh.request('set_location', 'devicegroup_code_history', product,  devicegroup);
    }

}

export default load_data;

